import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import Landing from "./pages/Landing";
import LiquidEngine from "./pages/LiquidEngine";
import DemoReel from "./pages/DemoReel";
import AdminAi from "./pages/AdminAi";
import MemeRadar from "./pages/MemeRadar";
import RhChain from "./pages/RhChain";
import { NFT_GATE_ENABLED, hasAccessPass } from "./lib/config";
import { WalletShell } from "./nft/wallet";
import { RippHome, RippCollection, RippViewer } from "./nft/pages";
import { MyRipps, HolderDash } from "./nft/holder";
import { RippToken } from "./nft/token";
import { RippLore } from "./nft/lore";
import { RippEngine } from "./nft/engine";
import { RippCinema } from "./nft/cinema";
import { RippRoadmap } from "./nft/roadmap";
import RippMint from "./nft/Mint";
import NftStudio from "./nft/NftStudio";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { useAgentSocket } from "./hooks/useAgentSocket";
import { api } from "./lib/api";
import { Panel } from "./components/Panel";
import { TopBar } from "./components/TopBar";
import { MarketWatch } from "./components/MarketWatch";
import { ControlPanel } from "./components/ControlPanel";
import { PriceChart } from "./components/PriceChart";
import { PositionIntelligence } from "./components/PositionIntelligence";
import DashboardV2 from "./pages/DashboardV2";
import DemoV2 from "./pages/DemoV2";
import { ManualEntry } from "./components/ManualEntry";
import { ConfluencePanel } from "./components/ConfluencePanel";
import { SignalLog } from "./components/SignalLog";
import { PositionsPanel } from "./components/PositionsPanel";
import { OrdersPanel } from "./components/OrdersPanel";
import { AccountPanel } from "./components/AccountPanel";
import { TradeHistory } from "./components/TradeHistory";
import { AlertsFeed } from "./components/AlertsFeed";
import { AiIntelligence } from "./components/AiIntelligence";
import { AiHud } from "./components/AiHud";
import { LiveControls } from "./components/LiveControls";
import { PendingPanel } from "./components/PendingPanel";
import { BacktestModal } from "./components/BacktestModal";
import { WaitlistModal } from "./components/WaitlistModal";
import { ForecastModal } from "./components/ForecastModal";
import { NewsModal } from "./components/NewsModal";
import { Login } from "./components/Login";
import { setAuthToken } from "./lib/api";
import { fmt } from "./lib/format";

function Dashboard({ onLogout }) {
  const { state, connected } = useAgentSocket();
  const [selected, setSelected] = useState("ES");
  const [tab, setTab] = useState("positions");
  const [equitySeries, setEquitySeries] = useState([]);
  const [backtestOpen, setBacktestOpen] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [forecastOpen, setForecastOpen] = useState(false);
  const [newsOpen, setNewsOpen] = useState(false);
  const [chartForecast, setChartForecast] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => api.forecast(selected, "15m", false).then((r) => { if (alive) setChartForecast(r?.forecast || null); }).catch(() => {});
    setChartForecast(null); load();
    const id = setInterval(load, 20000);
    return () => { alive = false; clearInterval(id); };
  }, [selected]);
  useEffect(() => {
    api.aiFocus(selected).catch(() => {});
  }, [selected]);
  const lastTs = useRef(null);
  const lastAlertId = useRef(null);
  const knownPos = useRef(new Set());

  // Auto-focus the chart on a newly opened live position so its ENTRY/SL/TP
  // markers always surface even if the user was viewing another symbol.
  useEffect(() => {
    if (!state) return;
    const syms = (state.positions || []).map((p) => p.root || p.symbol).filter(Boolean);
    const fresh = syms.find((s) => !knownPos.current.has(s));
    if (fresh) setSelected(fresh);
    knownPos.current = new Set(syms);
  }, [state]);

  useEffect(() => {
    if (!state?.ts || state.ts === lastTs.current) return;
    lastTs.current = state.ts;
    setEquitySeries((s) => {
      const next = [...s, { t: state.ts, equity: state.account.equity }];
      return next.slice(-150);
    });
    const alerts = state.alerts || [];
    if (alerts.length && alerts[0].id !== lastAlertId.current) {
      if (lastAlertId.current !== null && alerts[0].level === "TRADE") {
        toast(alerts[0].msg, { className: "font-mono text-xs" });
      }
      lastAlertId.current = alerts[0].id;
    }
  }, [state]);

  const call = async (fn, msg) => {
    try {
      await fn();
      if (msg) toast.success(msg, { className: "font-mono text-xs" });
    } catch {
      toast.error("Action failed", { className: "font-mono text-xs" });
    }
  };

  // Optimistic config override: a slider change reflects immediately and stays put regardless of
  // WebSocket/persistence timing. Each key auto-clears once the live state catches up to it.
  const [cfgOverride, setCfgOverride] = useState({});
  const mergedConfig = { ...(state?.config || {}), ...cfgOverride };
  const onConfig = async (p) => {
    const keys = ["confluence_threshold", "max_positions", "risk_per_trade", "max_daily_loss"];
    const flat = {};
    for (const k of keys) if (k in p) flat[k] = p[k];
    if (Object.keys(flat).length) setCfgOverride((o) => ({ ...o, ...flat }));
    try {
      const resp = await api.config(p);
      if (resp) setCfgOverride((o) => {
        const n = { ...o };
        for (const k of keys) if (k in resp) n[k] = resp[k];
        return n;
      });
    } catch {}
  };
  useEffect(() => {
    if (!state?.config) return;
    setCfgOverride((o) => {
      const n = { ...o }; let changed = false;
      for (const k of Object.keys(n)) if (state.config[k] === n[k]) { delete n[k]; changed = true; }
      return changed ? n : o;
    });
  }, [state]);

  if (!state) {
    return (
      <div className="min-h-screen bg-zinc-950 grid-bg flex items-center justify-center">
        <div className="text-zinc-400 text-sm flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-blue-500 live-dot" /> Connecting to trading engine…
        </div>
      </div>
    );
  }

  const market = state.market || [];
  const selMarket = market.find((m) => m.symbol === selected) || market[0];
  const selEv = state.evaluations?.[selected];
  const selPos = (state.positions || []).find((p) => p.symbol === selected || p.root === selected || (p.symbol && p.symbol.startsWith(selected)));
  const threshold = mergedConfig?.confluence_threshold || 6;

  return (
    <div className="h-screen w-full r2-bg text-zinc-100 p-2 flex flex-col gap-2 overflow-hidden">
      <TopBar
        state={state}
        connected={connected}
        onStart={() => call(api.start, "Agent started")}
        onStop={() => call(api.stop, "Agent stopped")}
        onKill={() => call(api.kill, "KILL SWITCH — all positions closed")}
        onReset={() => call(api.reset, "Account & history reset")}
        onBacktest={() => setBacktestOpen(true)}
        onWaitlist={() => setWaitlistOpen(true)}
        onForecast={() => setForecastOpen(true)}
        onNews={() => setNewsOpen(true)}
        onLogout={onLogout}
      />

      <div className="flex-1 min-h-0 grid grid-cols-12 gap-2">
        {/* LEFT */}
        <div className="col-span-3 flex flex-col gap-2 min-h-0">
          <Panel title="Market Watch" testid="market-watch"
            right={<span className="text-[9px] text-zinc-500 font-mono">{market.length} SYM</span>}
            className="flex-[0_0_auto] max-h-[45%]">
            <MarketWatch market={market} selected={selected} onSelect={setSelected} threshold={threshold} />
          </Panel>
          <Panel title="Control Panel" testid="control-panel" className="flex-1 min-h-0">
            <ControlPanel
              config={mergedConfig}
              onConfig={onConfig}
              extra={<LiveControls config={state.config} tv={state.tradovate} emailEnabled={state.email_enabled} traded={state.traded} sessions={state.sessions} />}
            />
          </Panel>
        </div>

        {/* CENTER */}
        <div className="col-span-7 flex flex-col gap-2 min-h-0">
          <Panel
            title={`Chart · ${selMarket?.symbol || ""}`}
            testid="price-chart"
            right={
              <div className="flex items-center gap-3 font-mono text-[11px]">
                <ManualEntry symbol={selected} connected={state.tradovate?.connected} agentStops={!!mergedConfig.manual_agent_stops} onToggleStops={(v) => call(() => api.manualStops(v), v ? "Agent stops ON for manual trades" : "Agent stops OFF — manual trades self-managed")} />
                <span className="h-4 w-px bg-zinc-800" />
                <span className="text-zinc-100 font-semibold">{fmt(selMarket?.price)}</span>
                <span className={selMarket?.change >= 0 ? "text-green-500" : "text-red-500"}>
                  {selMarket?.change >= 0 ? "+" : ""}{fmt(selMarket?.change_pct)}%
                </span>
              </div>
            }
            className="flex-[0_0_auto] h-[58%]"
          >
            <div className="relative h-full">
              <AiHud ai={state.ai} symbol={selected} />
              <PriceChart
              symbol={selected}
              market={selMarket}
              position={selPos}
              forecast={chartForecast}
              prognosis={state.ai?.prognosis}
              trend={state.ai?.trend}
              deskOrder={state.ai?.desk_order}
              pme={state.pme}
              onAdjust={selPos?.live ? (payload) => call(() => api.adjustBracket({ root: selPos.root || selected, ...payload }), "Live bracket updated") : null}
              />
            </div>
          </Panel>
          <Panel title="Live Confluence" testid="confluence" className="flex-[0_0_auto] max-h-[24%]">
            <ConfluencePanel ev={selEv} threshold={threshold} skipReason={selMarket?.skip_reason} />
          </Panel>
          <Panel title="Signal Log" testid="signal-log"
            right={<span className="text-[9px] text-zinc-500 font-mono">{(state.signals || []).length}</span>}
            className="flex-1 min-h-0">
            <SignalLog signals={state.signals} />
          </Panel>
        </div>

        {/* RIGHT */}
        <div className="col-span-2 flex flex-col gap-2 min-h-0">
          <Panel title="Account Summary" testid="account-summary" className="flex-[0_0_auto]">
            <AccountPanel account={state.account} equitySeries={equitySeries} />
          </Panel>

          <Panel
            title={
              <span className="flex gap-1">
                {["positions", "orders", "manage"].map((t) => (
                  <button
                    key={t}
                    data-testid={`tab-${t}`}
                    onClick={() => setTab(t)}
                    className={`uppercase text-[10px] tracking-wider ${tab === t ? "text-zinc-100" : "text-zinc-600 hover:text-zinc-400"}`}
                  >
                    {t}
                    {t === "positions" ? ` (${(state.positions || []).length})` : t === "orders" ? ` (${(state.orders || []).length})` : ` (${(state.pme?.positions || []).length})`}
                  </button>
                ))}
              </span>
            }
            testid="positions-orders"
            className="flex-[0_0_auto] h-[20%]"
          >
            {tab === "manage" ? (
              <PositionIntelligence pme={state.pme} />
            ) : tab === "positions" ? (
              <PositionsPanel positions={state.positions} onClose={(p) => call(() => (p.live ? api.flattenLive(p) : api.closePosition(p.id)), p.live ? "Live position flattened" : "Position closed")} onCloseAll={() => call(() => api.flattenAll(), "All positions flattened")} />
            ) : (
              <OrdersPanel orders={state.orders} />
            )}
          </Panel>

          <Panel
            title={
              <span className="flex items-center gap-1.5">
                Intelligence Desk
                {state.config?.desk_auto_execute ? (
                  <span data-testid="desk-mode-badge" className="rounded-sm border border-amber-500/40 bg-amber-500/10 px-1 text-[7.5px] font-mono tracking-[0.12em] text-amber-300">LIMITS ARMED</span>
                ) : state.ai?.shadow_mode && (
                  <span data-testid="desk-mode-badge" className="rounded-sm border border-blue-500/40 bg-blue-500/10 px-1 text-[7.5px] font-mono tracking-[0.12em] text-blue-300">SHADOW</span>
                )}
              </span>
            }
            testid="ai-intelligence"
            right={
              <span className="text-[9px] font-mono text-zinc-500">
                {state.ai?.consensus ? String(state.ai.consensus.agreement || "").replace(/_/g, " ") : (state.ai?.enabled ? "standby" : "off")}
              </span>
            }
            className="flex-[0_0_auto] h-[30%]">
            <AiIntelligence
              ai={state.ai}
              symbol={selected}
              onAnalyze={() => call(() => api.aiAnalyze(selected), "Agent desk analysing " + selected)}
            />
          </Panel>

          <Panel title="Trades Taken" testid="trade-history"
            right={<span className="text-[9px] text-zinc-500 font-mono">{(state.trades_taken || []).length}</span>}
            className="flex-1 min-h-0">
            <TradeHistory trades={state.trades_taken} />
          </Panel>

          <Panel title="Alerts" testid="alerts" className="flex-[0_0_auto] h-[15%]">
            <AlertsFeed alerts={state.alerts} />
          </Panel>
        </div>
      </div>

      <Toaster position="bottom-right" theme="dark" />
      <PendingPanel
        pending={state.pending}
        onConfirm={(id) => call(() => api.confirm(id, true), "Live order confirmed & sent")}
        onReject={(id) => call(() => api.confirm(id, false), "Order rejected")}
      />
      <BacktestModal open={backtestOpen} onOpenChange={setBacktestOpen} symbols={market.map((m) => m.symbol)} />
      <WaitlistModal open={waitlistOpen} onOpenChange={setWaitlistOpen} />
      <ForecastModal open={forecastOpen} onOpenChange={setForecastOpen} symbol={selected} />
      <NewsModal open={newsOpen} onOpenChange={setNewsOpen} symbol={selected} />
    </div>
  );
}

function AppShell() {
  // /app = sign-in, then the RIPP 2.0 command center. The legacy terminal lives at /classic.
  const [authed, setAuthed] = useState(!!(typeof localStorage !== "undefined" && localStorage.getItem("smc_token")));
  // NFT access gate — only enforced once NFT_GATE_ENABLED is turned on (after the pass launches).
  if (NFT_GATE_ENABLED && !hasAccessPass()) return <Navigate to="/mint" replace />;
  if (!authed) return <Login onAuthed={() => setAuthed(true)} />;
  return <Navigate to="/v2" replace />;
}

function ClassicShell() {
  const [authed, setAuthed] = useState(!!(typeof localStorage !== "undefined" && localStorage.getItem("smc_token")));
  if (NFT_GATE_ENABLED && !hasAccessPass()) return <Navigate to="/mint" replace />;
  if (!authed) return <Navigate to="/app" replace />;
  return <Dashboard onLogout={() => { setAuthToken(null); setAuthed(false); }} />;
}

function DemoV2Shell() {
  const authed = !!(typeof localStorage !== "undefined" && localStorage.getItem("smc_token"));
  if (!authed) return <Navigate to="/app" replace />;
  return <DemoV2 />;
}

function V2Shell() {
  const authed = !!(typeof localStorage !== "undefined" && localStorage.getItem("smc_token"));
  if (NFT_GATE_ENABLED && !hasAccessPass()) return <Navigate to="/mint" replace />;
  if (!authed) return <Navigate to="/app" replace />;
  return <DashboardV2 />;
}

function EngineShell() {
  const authed = !!(typeof localStorage !== "undefined" && localStorage.getItem("smc_token"));
  if (NFT_GATE_ENABLED && !hasAccessPass()) return <Navigate to="/mint" replace />;
  if (!authed) return <Navigate to="/app" replace />;
  return <LiquidEngine />;
}

function DemoShell() {
  const authed = !!(typeof localStorage !== "undefined" && localStorage.getItem("smc_token"));
  if (!authed) return <Navigate to="/app" replace />;
  return <DemoReel />;
}

function AdminShell() {
  const authed = !!(typeof localStorage !== "undefined" && localStorage.getItem("smc_token"));
  if (!authed) return <Navigate to="/app" replace />;
  return <AdminAi />;
}

function MemeShell() {
  const authed = !!(typeof localStorage !== "undefined" && localStorage.getItem("smc_token"));
  if (!authed) return <Navigate to="/app" replace />;
  return <MemeRadar />;
}

function RhChainShell() {
  const authed = !!(typeof localStorage !== "undefined" && localStorage.getItem("smc_token"));
  if (!authed) return <Navigate to="/app" replace />;
  return <RhChain />;
}

function NftAdminShell() {
  const authed = !!(typeof localStorage !== "undefined" && localStorage.getItem("smc_token"));
  if (!authed) return <Navigate to="/app" replace />;
  return <NftStudio />;
}

const W = (el) => <WalletShell>{el}</WalletShell>;

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/mint" element={<Navigate to="/ripp/mint" replace />} />
        <Route path="/ripp" element={W(<RippHome />)} />
        <Route path="/ripp/mint" element={W(<RippMint />)} />
        <Route path="/ripp/token" element={W(<RippToken />)} />
        <Route path="/ripp/lore" element={W(<RippLore />)} />
        <Route path="/ripp/roadmap" element={W(<RippRoadmap />)} />
        <Route path="/ripp/engine" element={W(<RippEngine />)} />
        <Route path="/ripp/cinema" element={W(<RippCinema />)} />
        <Route path="/ripp/collection" element={W(<RippCollection />)} />
        <Route path="/ripp/nft/:id" element={W(<RippViewer />)} />
        <Route path="/ripp/my-ripps" element={W(<MyRipps />)} />
        <Route path="/ripp/holder" element={W(<HolderDash />)} />
        <Route path="/ripp/holder/hive" element={W(<HolderDash hive />)} />
        <Route path="/admin/ripp-nft" element={<NftAdminShell />} />
        <Route path="/app" element={<AppShell />} />
        <Route path="/classic" element={<ClassicShell />} />
        <Route path="/engine" element={<EngineShell />} />
        <Route path="/v2" element={<V2Shell />} />
        <Route path="/v2/demo" element={<DemoV2Shell />} />
        <Route path="/demo" element={<DemoShell />} />
        <Route path="/admin/ai" element={<AdminShell />} />
        <Route path="/meme" element={<MemeShell />} />
        <Route path="/rhchain" element={<RhChainShell />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
